/* eslint-disable no-unused-vars */
import { useState, useEffect } from "react";
import "./UserDashboard.scss";

import person from "../../assets/person.png";
import ktp from "../../assets/ktp.jpg";
import logoOJK from "../../assets/OJK-logo.png";
import logoAFPI from "../../assets/AFPI-logo.png";
import { useNavigate } from "react-router-dom";
import { getUserData } from "../../api";
import { motion, AnimatePresence } from "framer-motion";
import { LogOut, CreditCard, User, FileText, Copy, ArrowLeft, Check } from "lucide-react";

export default function UserDashboard() {
  const [currentPage, setCurrentPage] = useState("form");
  const [userData, setUserData] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const navigate = useNavigate();
  const BASE_URL = "";

  useEffect(() => {
    const token =
      localStorage.getItem("userToken") || sessionStorage.getItem("userToken");
    if (!token) return;

    getUserData(token)
      .then((data) => {
        setUserData(data);
        const storage = localStorage.getItem("userToken")
          ? localStorage
          : sessionStorage;
        storage.setItem("userData", JSON.stringify(data));
      })
      .catch(() => {
        setUserData(null);
      });
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("userToken");
    sessionStorage.removeItem("userToken");
    navigate("/login-user");
  };

  // Ambil path dari userData
  const fotoKtp = userData?.info_pinjam[0]?.foto?.[0]?.foto_ktp;
  const fotoSelfie = userData?.info_pinjam[0]?.foto?.[0]?.foto_selfie;

  // Fungsi untuk menyalin ke clipboard
  const handleCopy = (value, id) => {
    if (!value || value === "-") return;
    navigator.clipboard.writeText(value);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const pageVariants = {
    initial: { opacity: 0, x: 20 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -20 }
  };

  return (
    <div className="dashboard-container">
      <header className="dashboard-header">
        <div className="header-brand">
          <CreditCard size={24} />
          <span>DuitKita Dashboard</span>
        </div>
        <div className="header-actions">
          <button className="logout-btn" onClick={handleLogout} title="Logout">
            <LogOut size={18} />
            <span className="mobile-hide">Keluar</span>
          </button>
        </div>
      </header>

      <main className="dashboard-content">
        <div className="welcome-section">
          <h1>Halo, {userData?.name || "Nasabah"}!</h1>
          <p>Berikut adalah informasi layanan pinjaman Anda.</p>
        </div>

        <AnimatePresence mode="wait">
          {currentPage === "form" ? (
            <motion.div
              key="form"
              className="dashboard-card"
              variants={pageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={{ duration: 0.3 }}
            >
              <div className="data-section">
                <div className="id-cards">
                  <h3>Dokumen</h3>
                  <img
                    src={fotoKtp ? `${BASE_URL}/${fotoKtp.replace(/\\/g, "/")}` : ktp}
                    alt="Foto KTP"
                  />
                  {/* Selfie suppressed as per original logic */}
                </div>

                <div className="info-form">
                  <div className="info-item">
                    <label>No Tagihan</label>
                    <div className="value">{userData?.info_pinjam[0]?.nomor_tagihan || "-"}</div>
                  </div>
                  <div className="info-item">
                    <label>Produk Pinjaman</label>
                    <div className="value">{userData?.info_pinjam[0]?.produk_pinjaman || "-"}</div>
                  </div>
                  <div className="info-item">
                    <label>Nama Nasabah</label>
                    <div className="value">{userData?.name || "-"}</div>
                  </div>
                  <div className="info-item">
                    <label>No Telepon</label>
                    <div className="value">{userData?.phone || "-"}</div>
                  </div>
                  <div className="info-item">
                    <label>No KTP</label>
                    <div className="value">{userData?.info_pinjam[0]?.no_ktp || "-"}</div>
                  </div>
                  <div className="info-item">
                    <label>Jatuh Tempo</label>
                    <div className="value">
                      {userData?.info_pinjam[0]?.tanggal_jatuh_tempo
                        ? new Date(userData?.info_pinjam[0]?.tanggal_jatuh_tempo).toLocaleDateString()
                        : "-"}
                    </div>
                  </div>
                  <div className="info-item full-width">
                    <label>Total Tagihan</label>
                    <div className="value" style={{ color: 'var(--primary-color)', fontSize: '1.2rem' }}>
                      {new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR' }).format(userData?.info_pinjam[0]?.total_tagihan || 0)}
                    </div>
                  </div>

                  <button className="pay-btn" onClick={() => setCurrentPage("bayar")}>
                    <CreditCard size={20} />
                    Bayar Sekarang
                  </button>
                </div>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="bayar"
              className="dashboard-card payment-page"
              variants={pageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={{ duration: 0.3 }}
            >
              <div className="header">
                <button onClick={() => setCurrentPage("form")}>
                  <ArrowLeft size={24} />
                </button>
                <h2>Halaman Pembayaran</h2>
              </div>

              <div className="pay-summary">
                <div className="info-item">
                  <label>Total yang harus dibayar</label>
                  <div className="value" style={{ fontSize: '1.5rem', color: 'var(--primary-color)' }}>
                    {new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR' }).format(userData?.info_pinjam[0]?.total_tagihan || 0)}
                  </div>
                </div>
              </div>

              <div className="payment-details">
                <h3>Metode Pembayaran (Virtual Account)</h3>

                <div className="virtual-account-card">
                  <h4>{userData?.info_pinjam[0]?.bank_virtual_account1 || "Bank VA 1"}</h4>
                  <div className="va-row">
                    <span className="va-number">{userData?.info_pinjam[0]?.nomor_virtual_account1 || "-"}</span>
                    <button onClick={() => handleCopy(userData?.info_pinjam[0]?.nomor_virtual_account1, 'va1')}>
                      {copiedId === 'va1' ? <Check size={14} /> : <Copy size={14} />}
                      {copiedId === 'va1' ? "Disalin!" : "Salin"}
                    </button>
                  </div>
                </div>

                <div className="virtual-account-card">
                  <h4>{userData?.info_pinjam[0]?.bank_virtual_account2 || "Bank VA 2"}</h4>
                  <div className="va-row">
                    <span className="va-number">{userData?.info_pinjam[0]?.nomor_virtual_account2 || "-"}</span>
                    <button onClick={() => handleCopy(userData?.info_pinjam[0]?.nomor_virtual_account2, 'va2')}>
                      {copiedId === 'va2' ? <Check size={14} /> : <Copy size={14} />}
                      {copiedId === 'va2' ? "Disalin!" : "Salin"}
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      <div className="footer-container">
        <img src={logoOJK} alt="OJK Logo" />
        <img src={logoAFPI} alt="AFPI Logo" />
      </div>
    </div>
  );
}
